// The platform audit log (spec/14 §15, spec/07 §11.2; F-SUP-06, #395): every organisation's rows and
// the platform's own, newest first, over `GET /v1/hq/audit` and `POST /v1/hq/audit/export`, for a
// role holding `audit:read` at platform scope (`hq_owner` by default).
//
//  1. **A reason first.** Nothing is asked of the api, and nothing is listed, until the reader has
//     said why they are reading. The api records every read — one platform-level row, and one
//     `audit.log.read` row in the log of every organisation whose rows came back, naming the reader
//     and the reason — and the page says so plainly, before and while it is used.
//  2. **Kept while the page is open.** The reason is sent with every read and export of this view —
//     each filter, each older page, each export — until the reader changes it or leaves the page;
//     coming back asks again. It is never stored.
//  3. **Trace a support call.** A request id (`req_…`) — pasted in the search box or in More filters,
//     or reached from a row's detail — shows exactly the rows that request wrote, and nothing else,
//     with its trace id and, when the build names a trace viewer, a link.
//  4. **Export** the filtered set as CSV, with the same reason. Past 10,000 rows the api refuses with
//     `422` rather than cut the file short, and the page says how to narrow it.
//
// The page itself is a product screen (BRAND.md §6.1): a header with its one action, a compact filter
// bar (`filter-bar.tsx`), dense rows that open in a centred dialog (`rows.tsx`, `row-detail.tsx`).

import type { AuditEntry, AuditEntryList } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Button, EmptyState, InfoTip, VisuallyHidden } from '@clavian/ui-web';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { useCallback, useId, useState, type ReactNode } from 'react';

import { useApi } from '../../api/client';
import { fieldErrors, problemCode, PROBLEM } from '../../api/problems';
import { queryKeys, useSession } from '../../api/queries';
import { Icon, NavIcon } from '../../shell/nav-icons';
import { LoadFailure } from '../../shell/LoadFailure';
import { Notice, Page, TableSkeleton } from '../../ui';
import { RequirePlatformPermission } from '../access';
import { ReasonField } from '../ReasonField';
import { AUDIT_READ } from '../sections';
import { FilterBar, type AuditView } from './filter-bar';
import {
  auditQuery,
  EXPORT_MAX_ROWS,
  fallbackFileName,
  fileNameOf,
  isRequestId,
  REASON_MAX,
  REASON_MIN,
  withRange,
  type AuditFilter,
  type AuditRange,
} from './filters';
import { useAuditLabels, useDirectory } from './labels';
import { RowDetail } from './row-detail';
import { AuditTable } from './rows';
import { saveFile } from './save-file';
import { useAuditTimes } from './time';
import { traceIdOf, traceUrlOf } from './trace';

import '../../org/audit/audit.css';

/** `/audit`. */
export function AuditLogPage(): ReactNode {
  return (
    <RequirePlatformPermission permission={AUDIT_READ}>
      <AuditLog />
    </RequirePlatformPermission>
  );
}

const WHOLE_LOG: AuditView = { filter: {}, range: 'all' };

function AuditLog() {
  const { t } = useTranslation('hq');
  // In this page's state and nowhere else: leaving the page forgets it (rule 2).
  const [reason, setReason] = useState<string | null>(null);
  // A link may name the request to trace (`/audit?requestId=req_…`); it waits for the reason too.
  const search = useSearch({ strict: false }) as Readonly<Record<string, unknown>>;
  const linked = typeof search['requestId'] === 'string' && isRequestId(search['requestId']) ? search['requestId'] : undefined;
  const initial: AuditView = linked === undefined ? WHOLE_LOG : { filter: { requestId: linked }, range: 'all' };
  const [view, setView] = useState<AuditView>(initial);
  const exporter = useExport(reason, view.filter);

  return (
    <Page
      title={t('audit.title')}
      meta={<InfoTip>{t('audit.intro')}</InfoTip>}
      actions={
        reason === null ? undefined : (
          <Button busy={exporter.isPending} onClick={() => exporter.mutate()}>
            {t('audit.export.submit')}
          </Button>
        )
      }
      className="console-audit"
    >
      {reason === null ? (
        <ReasonGate
          onReason={(given) => {
            setView(initial);
            setReason(given);
          }}
        />
      ) : (
        <>
          <ExportResult exporter={exporter} />
          <Reading
            reason={reason}
            view={view}
            onView={(next) => {
              exporter.reset();
              setView(next);
            }}
            onChangeReason={() => {
              exporter.reset();
              setReason(null);
            }}
          />
        </>
      )}
    </Page>
  );
}

// ---------------------------------------------------------------------------------------------
// 1. The reason
// ---------------------------------------------------------------------------------------------

function ReasonGate({ onReason }: { readonly onReason: (reason: string) => void }) {
  const { t } = useTranslation('hq');
  const [text, setText] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  return (
    <form
      className="console-hq-gate"
      aria-labelledby="console-audit-reason-title"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        const reason = text.trim();
        if (reason.length < REASON_MIN) {
          setError(t('audit.reason.tooShort', { count: REASON_MIN }));
          return;
        }
        onReason(reason);
      }}
    >
      <h2 id="console-audit-reason-title">{t('audit.reason.title')}</h2>
      <ReasonField
        label={t('audit.reason.label')}
        description={t('audit.reason.description')}
        value={text}
        maxLength={REASON_MAX}
        onChange={setText}
        error={error}
      />
      <div>
        <Button type="submit" variant="primary">
          {t('audit.reason.submit')}
        </Button>
      </div>
    </form>
  );
}

function ReasonInUse({ reason, onChange }: { readonly reason: string; readonly onChange: () => void }) {
  const { t } = useTranslation('hq');
  const session = useSession();
  const name = session.data?.user.name;
  return (
    <Notice
      className="console-audit-reason"
      title={t('audit.reason.inUse')}
      action={
        <Button size="sm" variant="ghost" onClick={onChange}>
          {t('audit.reason.change')}
        </Button>
      }
    >
      <p className="console-audit-reason__text">{reason}</p>
      <p className="console-audit-reason__note">{name === undefined || name === '' ? t('audit.reason.seenBy') : t('audit.reason.seenByName', { name })}</p>
    </Notice>
  );
}

// ---------------------------------------------------------------------------------------------
// Reading: the filter bar (or the trace banner), the rows, a row's detail
// ---------------------------------------------------------------------------------------------

function Reading({
  reason,
  view,
  onView,
  onChangeReason,
}: {
  readonly reason: string;
  readonly view: AuditView;
  readonly onView: (view: AuditView) => void;
  readonly onChangeReason: () => void;
}) {
  const labels = useAuditLabels();
  const directory = useDirectory();
  const times = useAuditTimes();
  // Where "Back to the whole log" returns: the filters as they were before a request was traced.
  const [beforeTrace, setBeforeTrace] = useState<AuditView>(WHOLE_LOG);
  const traced = view.filter.requestId;

  const trace = useCallback(
    (requestId: string) => {
      if (traced === undefined) setBeforeTrace(view);
      onView({ filter: { requestId }, range: 'all' });
    },
    [traced, view, onView],
  );
  // A filter without a window is the whole of time; one with a window keeps its quick range.
  const filtered = (filter: AuditFilter) =>
    onView({ filter, range: filter.from === undefined && filter.to === undefined ? 'all' : view.range });

  return (
    <>
      <ReasonInUse reason={reason} onChange={onChangeReason} />
      {traced === undefined ? (
        <FilterBar
          view={view}
          labels={labels}
          directory={directory}
          times={times}
          onFilter={filtered}
          onRange={(range: AuditRange) => onView({ filter: withRange(view.filter, range), range })}
          onApply={(filter) => {
            const sameWindow = filter.from === view.filter.from && filter.to === view.filter.to;
            const windowed = filter.from !== undefined || filter.to !== undefined;
            onView({ filter, range: !windowed ? 'all' : sameWindow ? view.range : 'custom' });
          }}
          onTrace={trace}
        />
      ) : (
        <TraceBanner requestId={traced} onWholeLog={() => onView(beforeTrace)} />
      )}
      <Rows reason={reason} view={view} labels={labels} directory={directory} times={times} onTrace={trace} onClear={() => onView(WHOLE_LOG)} />
    </>
  );
}

function TraceBanner({ requestId, onWholeLog }: { readonly requestId: string; readonly onWholeLog: () => void }) {
  const { t } = useTranslation('hq');
  const traceId = traceIdOf(requestId);
  const url = traceUrlOf(requestId);
  const titleId = useId();
  return (
    <section className="console-audit-trace" aria-labelledby={titleId}>
      <span className="console-audit-trace__icon" aria-hidden="true">
        <Icon name="info" />
      </span>
      <div className="console-audit-trace__body">
        <h2 id={titleId} className="console-audit-trace__title">
          {t('audit.trace.heading')}
        </h2>
        <p className="console-audit-trace__note">{t('audit.trace.banner')}</p>
        <dl className="console-audit-trace__ids">
          <div>
            <dt>{t('audit.trace.requestId')}</dt>
            <dd>
              <code>{requestId}</code>
            </dd>
          </div>
          {traceId === undefined ? null : (
            <div>
              <dt>{t('audit.trace.traceId')}</dt>
              <dd>
                <code>{traceId}</code>
              </dd>
            </div>
          )}
        </dl>
        {url === undefined ? null : (
          <a href={url} target="_blank" rel="noreferrer">
            {t('audit.trace.open')}
          </a>
        )}
      </div>
      <div className="console-audit-trace__action">
        <Button size="sm" onClick={onWholeLog}>
          {t('audit.trace.clear')}
        </Button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------------------------
// The rows
// ---------------------------------------------------------------------------------------------

function Rows({
  reason,
  view,
  labels,
  directory,
  times,
  onTrace,
  onClear,
}: {
  readonly reason: string;
  readonly view: AuditView;
  readonly labels: ReturnType<typeof useAuditLabels>;
  readonly directory: ReturnType<typeof useDirectory>;
  readonly times: ReturnType<typeof useAuditTimes>;
  readonly onTrace: (requestId: string) => void;
  readonly onClear: () => void;
}) {
  const { t } = useTranslation('hq');
  const api = useApi();
  const { filter } = view;
  const [open, setOpen] = useState<AuditEntry | null>(null);
  const log = useInfiniteQuery({
    queryKey: queryKeys.hqAudit(reason, filter),
    queryFn: ({ pageParam }): Promise<AuditEntryList> =>
      api.listPlatformAudit({ query: { ...auditQuery(filter), reason, ...(pageParam === undefined ? {} : { cursor: pageParam }) } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.page.hasMore && last.page.cursor !== null ? last.page.cursor : undefined),
    // Every read is recorded in the organisations it shows. Read when the reader asks — a filter, an
    // older page — never on focus or on a remount, and keep nothing once the page is left.
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
  });

  const traced = filter.requestId;
  const filtered = Object.keys(filter).length > 0;
  const rows = log.data?.pages.flatMap((page) => page.data) ?? [];
  const onOpen = useCallback((row: AuditEntry) => setOpen(row), []);

  return (
    <section className="console-audit-results" aria-labelledby="console-audit-rows-title" aria-busy={log.isFetching || undefined}>
      <VisuallyHidden>
        <h2 id="console-audit-rows-title">{t('audit.rows.title')}</h2>
      </VisuallyHidden>
      {log.data === undefined ? (
        log.isError ? (
          <ReadFailure error={log.error} onRetry={() => void log.refetch()} />
        ) : (
          <TableSkeleton rows={8} label={t('audit.rows.loading')} />
        )
      ) : (
        <>
          {rows.length === 0 ? null : (
            <p className="console-audit-count">
              {t(log.hasNextPage ? 'audit.rows.shownMore' : 'audit.rows.shown', { count: rows.length })}
            </p>
          )}
          <AuditTable
            rows={rows}
            caption={traced === undefined ? t('audit.rows.caption') : t('audit.rows.captionTrace', { id: traced })}
            empty={
              traced !== undefined ? (
                <EmptyState size="compact" icon={<NavIcon name="audit" />} title={t('audit.rows.emptyTrace')} />
              ) : filtered ? (
                <EmptyState
                  size="compact"
                  icon={<NavIcon name="audit" />}
                  title={t('audit.rows.empty')}
                  description={t('audit.rows.emptyDescription')}
                  action={<Button onClick={onClear}>{t('audit.filters.clear')}</Button>}
                />
              ) : (
                <EmptyState size="compact" icon={<NavIcon name="audit" />} title={t('audit.rows.emptyLog')} />
              )
            }
            labels={labels}
            directory={directory}
            times={times}
            onOpen={onOpen}
            now={log.dataUpdatedAt}
          />
          {log.hasNextPage ? (
            <div className="console-audit-more-rows">
              <Button busy={log.isFetchingNextPage} onClick={() => void log.fetchNextPage()}>
                {t('audit.rows.more')}
              </Button>
            </div>
          ) : null}
        </>
      )}
      <RowDetail
        row={open}
        labels={labels}
        directory={directory}
        times={times}
        now={log.dataUpdatedAt}
        onClose={() => setOpen(null)}
        onTrace={(requestId) => {
          setOpen(null);
          onTrace(requestId);
        }}
      />
    </section>
  );
}

function ReadFailure({ error, onRetry }: { readonly error: unknown; readonly onRetry: () => void }) {
  const { t } = useTranslation('hq');
  const refused = fieldErrors(error);
  if (problemCode(error) === PROBLEM.validationFailed && refused.length > 0) {
    return (
      <Notice tone="danger" role="alert" title={t('audit.rows.refused')}>
        <ul className="console-audit-refused">
          {refused.map((problem) => (
            <li key={`${problem.field} ${problem.message}`}>
              <code>{problem.field}</code>: {problem.message}
            </li>
          ))}
        </ul>
      </Notice>
    );
  }
  return <LoadFailure error={error} message={t('audit.rows.failed')} onRetry={onRetry} />;
}

// ---------------------------------------------------------------------------------------------
// The export
// ---------------------------------------------------------------------------------------------

function useExport(reason: string | null, filter: AuditFilter) {
  const api = useApi();
  return useMutation({
    mutationFn: async () => {
      let name: string | undefined;
      const csv = await api.exportPlatformAudit({
        body: { ...auditQuery(filter), reason: reason ?? '' },
        headers: { accept: 'text/csv, application/problem+json' },
        onResponse: (head) => {
          name = fileNameOf(head.headers.get('content-disposition'));
        },
      });
      return { csv, name: name ?? fallbackFileName(filter) };
    },
    onSuccess: ({ csv, name }) => saveFile(name, csv, 'text/csv;charset=utf-8'),
  });
}

function ExportResult({ exporter }: { readonly exporter: ReturnType<typeof useExport> }) {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  return (
    <div className="console-audit-export">
      <p role="status" className={exporter.isSuccess ? 'console-audit-export__done' : 'console-audit-export__idle'}>
        {exporter.isSuccess ? t('audit.export.done', { file: exporter.data.name }) : ''}
      </p>
      {exporter.isError ? (
        problemCode(exporter.error) === PROBLEM.unprocessable ? (
          <Notice tone="warn" role="alert">
            {t('audit.export.tooMany', { max: format.number(EXPORT_MAX_ROWS) })}
          </Notice>
        ) : (
          <LoadFailure error={exporter.error} message={t('audit.export.failed')} />
        )
      ) : null}
    </div>
  );
}
